import { useAppDispatch, useAppSelector } from 'src/store';
import { ProgressStatus, SamplingEvent, SurveySamplingCadenceType } from 'src/shared/types';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { useEffect } from 'react';
import { useLazySurveyDesignsQuery } from 'src/app/surveys/state/api/surveyGraphSlice';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { useCreateSurveyDesignMutation, useHasSurveyDesignQuery } from '../state/api/newSurveyDesignGraphSlice';
import { getCurrentSurveyDesignFormState, getCurrentSurveyDesignFormStep } from '../state/newSurveyDesignSelector';
import { NewSurveyDesignFormState, setFormState } from '../state/newSurveyDesignSlice';

const useSurveyDesign = () => {
    const formStep = useAppSelector(getCurrentSurveyDesignFormStep);
    const formState = useAppSelector(getCurrentSurveyDesignFormState);
    const { currentCustomerId } = useCustomer();
    const navigate = useAppNavigation();

    const { data: surveyDesignAvailabilityData, isFetching: isCheckingSurveyDesignPresence } = useHasSurveyDesignQuery(
        {
            projectId: formState.projectId,
        },
        {
            skip: !formState.projectId,
            refetchOnMountOrArgChange: true,
        }
    );

    const dispatch = useAppDispatch();

    const onFormChange = (fragment: Partial<NewSurveyDesignFormState>) => {
        dispatch(
            setFormState({
                ...fragment,
            })
        );
    };

    useEffect(() => {
        if (surveyDesignAvailabilityData) {
            onFormChange({
                surveyDesignCompletionStatus: surveyDesignAvailabilityData.hasSurveyDesign,
            });
        }
    }, [surveyDesignAvailabilityData, isCheckingSurveyDesignPresence]);

    const {
        projectName,
        projectId,
        projectCode,
        subscriptionType,
        surveyPublishDate,
        surveySamples,
        country,
        projectLeadRole,
        projectLeadEmail,
        projectLeadName,
        samplingCadence,
        samplingEventsPerYear,
        samplingEvents,
        sampleGroups,
        habitatAssayTypes,
        surveyDesignCompletionStatus,
    } = formState;

    const { exists, metadata } = surveyDesignCompletionStatus || {};
    const isInProgressByCurrentUser =
        surveyDesignCompletionStatus && exists && metadata?.status === ProgressStatus.PENDING && metadata?.isCreatedByCurrentUser;
    const isInProgressByDifferentUser =
        surveyDesignCompletionStatus && exists && metadata?.status === ProgressStatus.PENDING && !metadata?.isCreatedByCurrentUser;
    const canCurrentUserContinueForm = surveyDesignCompletionStatus && (!exists || isInProgressByCurrentUser);

    const [createSurveyDesign] = useCreateSurveyDesignMutation();
    const [fetchSurveyDesigns] = useLazySurveyDesignsQuery();

    useEffect(() => {
        if ([!isInProgressByCurrentUser, surveyPublishDate, country, projectLeadRole].some(Boolean)) {
            return;
        }

        const fetchAndRestore = async () => {
            const data = await fetchSurveyDesigns({
                customerId: currentCustomerId,
                SurveyDesignStatus: ProgressStatus.PENDING,
            }).unwrap();
            const allPendingProjects = data?.listSurveyDesigns;
            const existingDefinition = allPendingProjects?.find(entry => entry.projectId === projectId);

            if (!existingDefinition) {
                return;
            }

            onFormChange({
                subscriptionType: existingDefinition.subscriptionType,
                country: existingDefinition.country,
                projectCode: existingDefinition.projectCode,
                projectLeadRole: existingDefinition.leadRole,
                projectLeadEmail: existingDefinition.leadEmail,
                projectLeadName: existingDefinition.leadName,
                samplingCadence: existingDefinition.samplingCadence?.type,
                samplingEventsPerYear: existingDefinition.samplingCadence?.samplingEventsPerYear,
                samplingEvents: existingDefinition.samplingCadence?.events,
                sampleGroups: existingDefinition.sampleGroups,
                habitatAssayTypes: existingDefinition.habitatAssayTypes,
                surveyPublishDate: existingDefinition.surveyPublishDate,
                surveySamples: existingDefinition.surveySamples,
            });
        };
        fetchAndRestore();
    }, [isInProgressByCurrentUser]);

    const submitSurveyDesignForm = async () => {
        if (!currentCustomerId || !samplingCadence) {
            return;
        }

        try {
            await createSurveyDesign({
                customerId: currentCustomerId,
                projectName,
                projectId,
                projectCode,
                subscriptionType: subscriptionType,
                surveyPublishDate,
                country,
                projectLeadRole,
                projectLeadEmail,
                projectLeadName,
                samplingCadenceType: samplingCadence as SurveySamplingCadenceType,
                samplingEventsPerYear,
                samplingEvent: samplingEvents,
                sampleGroups,
                habitatAssayTypes,
                status: ProgressStatus.COMPLETED,
                surveySamples,
            }).unwrap();
        } catch (error) {
            console.error('rejected', error);
        }
    };

    const saveSurveyDesignForm = async () => {
        if (!currentCustomerId || !projectId) {
            return;
        }

        try {
            await createSurveyDesign({
                customerId: currentCustomerId,
                projectName,
                projectId,
                projectCode,
                subscriptionType: subscriptionType,
                surveyPublishDate,
                country,
                projectLeadRole,
                projectLeadEmail,
                projectLeadName,
                samplingCadenceType: (samplingCadence as SurveySamplingCadenceType) || SurveySamplingCadenceType.SINGLE_YEAR,
                samplingEventsPerYear,
                samplingEvent: samplingEvents,
                sampleGroups,
                habitatAssayTypes,
                status: ProgressStatus.PENDING,
                surveySamples,
            }).unwrap();

            await fetchSurveyDesigns({
                customerId: currentCustomerId,
            }).unwrap();

            navigate.toSurveyAdmin();
        } catch (error) {
            console.error('rejected', error);
        }
    };

    const hasCompletedCurrentStep = () => {
        switch (formStep) {
            case 0:
                return [projectName, subscriptionType, country, projectLeadRole, surveyPublishDate].every(Boolean);
            case 1:
                // Sample groups and habitat assay types are no longer required
                // No validation required for step 1
                return true;

            case 2:
                if (!samplingCadence || !samplingEventsPerYear) {
                    return false;
                }

                const isValidSamplingEvent = (event: Omit<SamplingEvent, 'kits'>) => {
                    if ([event?.name, event?.fromDate, event?.toDate].every(Boolean)) {
                        const fromTimestamp = new Date(event.fromDate).getTime();
                        const toTimestamp = new Date(event.toDate).getTime();
                        return fromTimestamp < toTimestamp;
                    }
                    return false;
                };

                const validEvents = samplingEvents.map(isValidSamplingEvent);

                return Array(samplingEventsPerYear)
                    .fill('')
                    .every((_, index) => {
                        return validEvents[index];
                    });
            case 3:
                const { uploadedFileErrors } = formState;
                return [!uploadedFileErrors.length, surveySamples.length].every(Boolean);
            default:
                return true;
        }
    };

    return {
        hasCompletedCurrentStep,
        submitSurveyDesignForm,
        saveSurveyDesignForm,
        onFormChange,
        formState,
        formStep,
        isInProgressByDifferentUser,
        isDefinitionExists: exists,
        canCurrentUserContinueForm,
        isCheckingSurveyDesignPresence,
    };
};

export default useSurveyDesign;
